import { Asset } from './Asset';
import { BuildConfiguration } from './BuildConfiguration';
import { Organization } from '../Organization/Organization';
import { IFormat, FORMAT_VALIDATION_RULES } from '../Format/Format';
import { IFormatVersion } from '../Format/FormatVersion';
import { Country } from '../Location/Country';
import { CampaignParameter } from "./CampaignParameter";
import { ICanvasContent } from '../Format/CanvasContent';
import { IThirdPartyTracker } from '../Tracking/ThirdPartyTracker';

export interface ICampaign {
	id?: number;
	dateCreated?: string;
	dateLastChanged?: string;
	dateDeleted?: string;
	deleted?: boolean;
	creativeCode?: string;
	clickUrl?: string;
	description?: string;
	assets?: Asset[];
	type: CampaignType;
	buildConfiguration?: string;
	buildConfigurationModel: BuildConfiguration;
	width?: string;
	height?: string;
	campaignName?: string;
	countryModel?: Country;
	countryIsoCode?: string;
	published?: boolean;
	organization?: Organization;
	organizationId?: number;
	advertiser?: Organization;
	advertiserId?: number;
	format?: IFormat;
	formatId?: string;
	formatVersion?: IFormatVersion;
	formatVersionId?: string;
	parameters?: CampaignParameter[];
	canvasContent?: ICanvasContent[];
	thirdPartyTrackers?: IThirdPartyTracker[];
}

export class Campaign implements ICampaign {
	id: number;
	dateCreated: string;
	dateLastChanged: string;
	dateDeleted: string;
	deleted: boolean;
	creativeCode: string;
	clickUrl: string;
	description: string;
	assets: Asset[];
	type: CampaignType;
	buildConfiguration: string;
	buildConfigurationModel: BuildConfiguration;
	width?: string;
	height?: string;
	campaignName: string;
	countryModel: Country;
	countryIsoCode: string;
	published: boolean;
	organization: Organization;
	organizationId: number;
	advertiser: Organization;
	advertiserId: number;
	format?: IFormat;
	formatId?: string;
	formatVersion?: IFormatVersion;
	formatVersionId?: string;
	parameters?: CampaignParameter[];
	canvasContent?: ICanvasContent[];
	thirdPartyTrackers?: IThirdPartyTracker[];

	constructor();
	constructor(obj: ICampaign);
	constructor(obj?: ICampaign) {
		this.creativeCode = (obj && obj.creativeCode) || undefined;
		this.clickUrl = (obj && obj.clickUrl) || undefined;
		this.description = (obj && obj.description) || undefined;
		this.assets = (obj && obj.assets) || [];
		this.type = (obj && obj.type) || undefined;
		this.buildConfiguration = (obj && obj.buildConfiguration) || undefined;
		this.buildConfigurationModel = obj.buildConfigurationModel || undefined;
		this.width = (obj && obj.width) || undefined;
		this.height = (obj && obj.height) || undefined;
		this.campaignName = (obj && obj.campaignName) || undefined;
		this.countryModel = (obj && obj.countryModel) || undefined;
		this.countryIsoCode = (obj && obj.countryIsoCode) || undefined;
		this.published = (obj && obj.published) || undefined;
		this.organization = (obj && obj.organization) || undefined;
		this.organizationId = (obj && obj.organizationId) || undefined;
		this.advertiser = (obj && obj.advertiser) || undefined;
		this.advertiserId = (obj && obj.advertiserId) || undefined;
		this.format = (obj && obj.format) || undefined;
		this.formatId = (obj && obj.formatId) || undefined;
		this.formatVersion = (obj && obj.formatVersion) || undefined;
		this.formatVersionId = (obj && obj.formatVersionId) || undefined;
		this.parameters = obj?.parameters || [];
		this.canvasContent = obj?.canvasContent || [];
		this.thirdPartyTrackers = obj?.thirdPartyTrackers || [];
	}
}

export enum CampaignType {
	skin = 1,
	desktop_interscroll = 2,
	topscroll = 3,
	adform_vskin = 4,
	desktop_fullscreen_interscroll = 5,
	inread_expander = 6,
	mobil_interscroll = 7,
	sideskin = 8,
	simple_banner_template = 9,
	video_skin = 10,
	html_topscroll = 11,
	html_interscroll = 12,
	vertical_video_interscroll = 13,
	video_topscroll = 14,
	video_banner_template = 15,
	hosting = 16,
	desktop_html_interscroll = 17,
	image_banner_template = 18,
	video_desktop_interscroll = 19,
	fullpage_overlay_mobile_html = 20,
	fullpage_overlay_desktop_html = 21,
	unstable_format_template = 99,
	generic_format = 100,
	topscroll_desktop_image = 201,
	topscroll_desktop_video = 202,
	topscroll_desktop_html = 203,
}

export const CampaignTypeLabel = new Map<CampaignType, string>([
	[CampaignType.skin, 'Image Skin'],
	[CampaignType.sideskin, 'Fluid Skin'],
	[CampaignType.mobil_interscroll, 'Midscroll Mobile Image'],
	[CampaignType.vertical_video_interscroll, 'Midscroll Mobile Video'],
	[CampaignType.desktop_interscroll, 'Midscroll Desktop Standard Image'],
	[CampaignType.desktop_html_interscroll, 'Midscroll Desktop Fullscreen HTML'],
	[
		CampaignType.desktop_fullscreen_interscroll,
		'Midscroll Desktop Fullscreen Image ',
	],
	[
		CampaignType.video_desktop_interscroll,
		'Midscroll Desktop Fullscreen Video',
	],
	[CampaignType.html_interscroll, 'Midscroll Mobile HTML'],
	[CampaignType.topscroll, 'Topscroll Mobile Image'],
	[CampaignType.video_topscroll, 'Topscroll Mobile Video'],
	[CampaignType.html_topscroll, 'Topscroll Mobile HTML'],
	[CampaignType.topscroll_desktop_image, 'Topscroll Desktop Image'],
	[CampaignType.topscroll_desktop_video, 'Topscroll Desktop Video'],
	[CampaignType.topscroll_desktop_html, 'Topscroll Desktop HTML'],
	[CampaignType.inread_expander, 'Inread Expander Desktop'],
	[CampaignType.hosting, 'Hosting'],
	[CampaignType.simple_banner_template, 'Simple Banner Template'],
	[CampaignType.video_skin, 'Video Skin'],
	[CampaignType.video_banner_template, 'Video Banner Template'],
	[CampaignType.unstable_format_template, 'Unstable Format'],
	[CampaignType.image_banner_template, 'Image Banner Template'],
	[CampaignType.generic_format, 'Generic Format'],
	[CampaignType.fullpage_overlay_mobile_html, 'Fullpage Overlay Mobile HTML'],
	[CampaignType.fullpage_overlay_desktop_html, 'Fullpage Overlay Desktop HTML'],
]);

export const CampaignTypeFromLabel = new Map<string, CampaignType>([
	[
		CampaignTypeLabel.get(CampaignType.topscroll_desktop_image),
		CampaignType.topscroll_desktop_image,
	],
	[
		CampaignTypeLabel.get(CampaignType.topscroll_desktop_video),
		CampaignType.topscroll_desktop_video,
	],
	[
		CampaignTypeLabel.get(CampaignType.topscroll_desktop_html),
		CampaignType.topscroll_desktop_html,
	],
	[CampaignTypeLabel.get(CampaignType.skin), CampaignType.skin],
	[
		CampaignTypeLabel.get(CampaignType.desktop_interscroll),
		CampaignType.desktop_interscroll,
	],
	[CampaignTypeLabel.get(CampaignType.topscroll), CampaignType.topscroll],
	[
		CampaignTypeLabel.get(CampaignType.desktop_fullscreen_interscroll),
		CampaignType.desktop_fullscreen_interscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.inread_expander),
		CampaignType.inread_expander,
	],
	[
		CampaignTypeLabel.get(CampaignType.mobil_interscroll),
		CampaignType.mobil_interscroll,
	],
	[CampaignTypeLabel.get(CampaignType.sideskin), CampaignType.sideskin],
	[
		CampaignTypeLabel.get(CampaignType.simple_banner_template),
		CampaignType.simple_banner_template,
	],
	[CampaignTypeLabel.get(CampaignType.video_skin), CampaignType.video_skin],
	[
		CampaignTypeLabel.get(CampaignType.html_topscroll),
		CampaignType.html_topscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.html_interscroll),
		CampaignType.html_interscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.vertical_video_interscroll),
		CampaignType.vertical_video_interscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.video_topscroll),
		CampaignType.video_topscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.video_banner_template),
		CampaignType.video_banner_template,
	],
	[
		CampaignTypeLabel.get(CampaignType.unstable_format_template),
		CampaignType.unstable_format_template,
	],
	[CampaignTypeLabel.get(CampaignType.hosting), CampaignType.hosting],
	[
		CampaignTypeLabel.get(CampaignType.desktop_html_interscroll),
		CampaignType.desktop_html_interscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.image_banner_template),
		CampaignType.image_banner_template,
	],
	[
		CampaignTypeLabel.get(CampaignType.video_desktop_interscroll),
		CampaignType.video_desktop_interscroll,
	],
	[
		CampaignTypeLabel.get(CampaignType.generic_format),
		CampaignType.generic_format,
	],
	[
		CampaignTypeLabel.get(CampaignType.fullpage_overlay_mobile_html),
		CampaignType.fullpage_overlay_mobile_html,
	],
	[
		CampaignTypeLabel.get(CampaignType.fullpage_overlay_desktop_html),
		CampaignType.fullpage_overlay_desktop_html,
	],
]);

export const CAMPAIGN_VALIDATION_RULES: {
	campaignName: ((v: any) => any)[];
	description: ((v: any) => any)[];
	country: ((v: any) => any)[];
	formatTitle: ((v: any) => any)[];
	format: ((v: any) => any)[];
	width: ((v: any) => any)[],
	height: ((v: any) => any)[],
} = {
	campaignName: [
		(v) => !!v || 'Campaign name is required',
		(v) => (!!v && v.length > 6) || 'Campaign name be 6 characters or more',
		(v) =>
			(!!v && v.length <= 80) ||
			'Campaign name cannot contain more than 80 characters',
	],
	description: [
		(v) => !!v || 'Campaign name is required',
		(v) => (!!v && v.length > 6) || 'Campaign name be 6 characters or more',
		(v) =>
			(!!v && v.length <= 250) ||
			'Campaign name cannot contain more than 250 characters',
	],
	country: [(v) => !!v || 'country is required'],
	formatTitle: FORMAT_VALIDATION_RULES.title,
	format: [(v) => !!v || 'Format is required'],
	width: [
		v => !!v && v.length > 0 || 'Width is required',
		v => {
			let rule = /^[\d]*(%|px)$/;
			return !!v && rule.test(v) || 'Width has to be numeric and end with either % or px.'
		}
	],
	height: [
		v => !!v && v.length > 0 || 'Height is required',
		v => {
			let rule = /^[\d]*(%|px)$/;
			return !!v && rule.test(v) || 'Height has to be numeric and end with either % or px.'
		}
	],
};

export const FormatsOld: { id: string, title: string, type: number, description: string, hidden: boolean }[] =
[
	{
		id: "skin",
		title: CampaignTypeLabel.get(CampaignType.skin),
		type: CampaignType.skin,
		description: "",
		hidden: false
	},
	{
		id: "sideskin",
		title: CampaignTypeLabel.get(CampaignType.sideskin),
		type: CampaignType.sideskin,
		description: "",
		hidden: true
	},
	{
		id: "mobil_interscroll",
		title: CampaignTypeLabel.get(CampaignType.mobil_interscroll),
		type: CampaignType.mobil_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "vertical_video_interscroll",
		title: CampaignTypeLabel.get(CampaignType.vertical_video_interscroll),
		type: CampaignType.vertical_video_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "desktop_interscroll",
		title: CampaignTypeLabel.get(CampaignType.desktop_interscroll),
		type: CampaignType.desktop_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "desktop_html_interscroll",
		title: CampaignTypeLabel.get(CampaignType.desktop_html_interscroll),
		type: CampaignType.desktop_html_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "desktop_fullscreen_interscroll",
		title: CampaignTypeLabel.get(CampaignType.desktop_fullscreen_interscroll),
		type: CampaignType.desktop_fullscreen_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "video_desktop_interscroll",
		title: CampaignTypeLabel.get(CampaignType.video_desktop_interscroll),
		type: CampaignType.video_desktop_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "html_interscroll",
		title: CampaignTypeLabel.get(CampaignType.html_interscroll),
		type: CampaignType.html_interscroll,
		description: "",
		hidden: true
	},
	{
		id: "topscroll",
		title: CampaignTypeLabel.get(CampaignType.topscroll),
		type: CampaignType.topscroll,
		description: "",
		hidden: true
	},
	{
		id: "topscroll_desktop_image",
		title: CampaignTypeLabel.get(CampaignType.topscroll_desktop_image),
		type: CampaignType.topscroll_desktop_image,
		description: "",
		hidden: true
	},
	{
		id: "video_topscroll",
		title: CampaignTypeLabel.get(CampaignType.video_topscroll),
		type: CampaignType.video_topscroll,
		description: "",
		hidden: true
	},
	{
		id: "topscroll_desktop_video",
		title: CampaignTypeLabel.get(CampaignType.topscroll_desktop_video),
		type: CampaignType.topscroll_desktop_video,
		description: "",
		hidden: true
	},
	{
		id: "html_topscroll",
		title: CampaignTypeLabel.get(CampaignType.html_topscroll),
		type: CampaignType.html_topscroll,
		description: "",
		hidden: true
	},
	{
		id: "topscroll_desktop_html",
		title: CampaignTypeLabel.get(CampaignType.topscroll_desktop_html),
		type: CampaignType.topscroll_desktop_html,
		description: "",
		hidden: true
	},
	{
		id: "inread_expander",
		title: CampaignTypeLabel.get(CampaignType.inread_expander),
		type: CampaignType.inread_expander,
		description: "",
		hidden: false
	},
	{
		id: "fullpage_overlay_desktop_html",
		title: CampaignTypeLabel.get(CampaignType.fullpage_overlay_desktop_html),
		type: CampaignType.fullpage_overlay_desktop_html,
		description: "",
		hidden: false
	},
	{
		id: "fullpage_overlay_mobile_html",
		title: CampaignTypeLabel.get(CampaignType.fullpage_overlay_mobile_html),
		type: CampaignType.fullpage_overlay_mobile_html,
		description: "",
		hidden: false
	}
];
